import Typography from "@mui/material/Typography";
import { FilterChip } from "../FilterChip";
import { IndustriesPayload } from "./types";
import { IndustriesFilter } from "./IndustriesFilter";
import { INDUSTRY_TYPES } from "./constants";
import React, { useCallback, useMemo } from "react";
import { formatCountLabel } from "../../../utils/text";
import { HgNaics, HgSic } from "../../../types/taxonomy";
import { useTaxonomyContext } from "../../../contexts/Taxonomy/TaxonomyContext";
import { transformToTree } from "../../../utils/filters/utils";
import { useHierarchyRelations } from "../../../hooks/useHierarchyRelations";

interface IndustriesFilterChipProps {
  selectedValues: IndustriesPayload;
  onValuesChange: (payload: IndustriesPayload) => void;
  id?: string;
  highlighted?: boolean;
}

const useGetHighestLevelCounts = (selectedValues: IndustriesPayload) => {
  const { naics, sic } = useTaxonomyContext();
  const sicTree = transformToTree(sic as HgSic[], (sic) => sic.name);
  const naicsTree = transformToTree(naics as HgNaics[], (naics) => naics.name);

  const { filterToHighestAncestors: filterSicsToHighestAncestors } =
    useHierarchyRelations(sicTree);
  const { filterToHighestAncestors: filterNaicsToHighestAncestors } =
    useHierarchyRelations(naicsTree);

  return {
    industryCount: selectedValues.industryIds?.length || 0,
    naicsCount: filterNaicsToHighestAncestors(selectedValues.naicsIds || [])
      .length,
    sicCount: filterSicsToHighestAncestors(selectedValues.sicIds || []).length,
  };
};

export const IndustriesFilterChip: React.FC<IndustriesFilterChipProps> = ({
  selectedValues,
  onValuesChange,
  id = "industries",
  highlighted,
}: IndustriesFilterChipProps) => {
  const { industryCount, naicsCount, sicCount } =
    useGetHighestLevelCounts(selectedValues);

  const handleValuesChange = useCallback(
    (payload: IndustriesPayload) => onValuesChange(payload),
    [onValuesChange]
  );

  const selectedIndustryType = useMemo(() => {
    if (industryCount > 0) return INDUSTRY_TYPES.INDUSTRY;
    if (sicCount > 0) return INDUSTRY_TYPES.SIC;
    if (naicsCount > 0) return INDUSTRY_TYPES.NAICS;
    return INDUSTRY_TYPES.INDUSTRY;
  }, [industryCount, sicCount, naicsCount]);

  const getSelectedCount = useCallback(() => {
    switch (selectedIndustryType) {
      case INDUSTRY_TYPES.INDUSTRY:
        return industryCount;
      case INDUSTRY_TYPES.SIC:
        return sicCount;
      case INDUSTRY_TYPES.NAICS:
        return naicsCount;
      default:
        return 0;
    }
  }, [selectedIndustryType, industryCount, sicCount, naicsCount]);

  return (
    <FilterChip
      id={id}
      label="Industry"
      getSelectedCount={getSelectedCount}
      highlighted={highlighted}
      popoverOptions={{ disableActionButtons: true }}
      renderLabel={(label, count) => {
        return (
          <Typography component="span" color="textSecondary">
            {formatCountLabel(label, count)}
          </Typography>
        );
      }}
    >
      <IndustriesFilter
        id={`${id}-filter`}
        selectedValues={selectedValues}
        onValuesChange={handleValuesChange}
      />
    </FilterChip>
  );
};
